import React, {useCallback, useState} from "react"
import {SiteBasedAlertSelector} from "@/components/paginated-selector/site-based-alert-selector";
import {Link, useNavigate} from "react-router-dom";
import {ButtonV2} from "@/components/ui/ButtonsV2/ButtonV2";
import {apiV2} from "@/api/axiosConfig";
import {ConfirmationModal} from "@/components/modals/ConfirmationModal";
import {Action, ActionSelect} from "@/components/ui/ActionSelect";
import {useCopyID} from "@/lib/misc/useCopyID";

type SiteBasedAlert = {
    id: string;
    name: string;
    clientID: string;
    clientName: string;
    locationType: string;
    incidentType: string;
}

export function SiteAlertsListPage() {
    const navigate = useNavigate();
    const [refreshKey, setRefreshKey] = useState(0);
    const {copyID, isCopied} = useCopyID();

    function alertsUrl() {
        return window.location.pathname + window.location.search;
    }
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [alertToDelete, setAlertToDelete] = useState<SiteBasedAlert | null>(null);

    const handleDeleteClick = useCallback((alert: SiteBasedAlert) => {
        setAlertToDelete(alert);
        setDeleteModalOpen(true);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        if (!alertToDelete) return;

        try {
            await apiV2.delete(`site-based-alerts/${alertToDelete.id}`);
            setRefreshKey(prev => prev + 1);
            setAlertToDelete(null);
        } catch (error) {
            console.error("Failed to delete alert:", error);
            alert("Failed to delete alert");
        }
    }, [alertToDelete]);

    const handleColumnClick = useCallback((alert: SiteBasedAlert, columnIndex: number) => {
        // Column 0 is the alert name
        if (columnIndex === 0) {
           navigate(`/site-alerts/edit/${alert.id}?prev=${encodeURIComponent(alertsUrl())}`);
        }

        // Column 1 is the client name
        if(columnIndex === 1) {
            window.open(`/management/clients/edit/${alert.clientID}`, "_blank");
        }
    }, []);

    const renderActions = useCallback((alert: SiteBasedAlert) => {
        return <ActionSelect label={"Select Action"}>
            <Action label={"Edit"} onSelect={() => navigate(`/site-alerts/edit/${alert.id}?prev=${encodeURIComponent(alertsUrl())}`)}/>
            <Action label={"Delete"} onSelect={() => handleDeleteClick(alert)}/>
            <Action label={isCopied(alert.id) ? "Copied!" : "Copy ID"} onSelect={() => copyID(alert.id)} keepOpen />
        </ActionSelect>
    }, [handleDeleteClick, copyID, isCopied]);

    return (
        <div className="flex flex-col gap-4">
            <nav className="flex items-center gap-2 text-sm text-light-grey">
                <Link to="/sites" className="hover:text-grey">Sites</Link>
                <span>&gt;</span>
                <span className="text-grey">Site-Based Alerts</span>
            </nav>
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-semibold">Site-Based Alerts</h1>
                <div className="flex gap-2 flex-shrink-0">
                    <ButtonV2
                        label="Import Alerts"
                        onClick={() => navigate(`/site-alerts/import?prev=${encodeURIComponent(alertsUrl())}`)}
                    />
                    <ButtonV2
                        label="Create Alert"
                        onClick={() => navigate(`/site-alerts/create?prev=${encodeURIComponent(alertsUrl())}`)}
                    />
                </div>
            </div>
            <div className="">
                <SiteBasedAlertSelector
                    key={refreshKey}
                    renderActions={renderActions}
                    onColumnClick={handleColumnClick}
                />
            </div>

            <ConfirmationModal
                open={deleteModalOpen}
                setOpen={setDeleteModalOpen}
                title="Delete Site-Based Alert"
                message={`Are you sure you want to delete "${alertToDelete?.name}"? This action cannot be undone.`}
                confirmLabel="Delete"
                cancelLabel="Cancel"
                confirmButtonColor="red"
                onConfirm={handleDeleteConfirm}
            />
        </div>
    )
}
